import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { contactsAPI, companiesAPI, employeesAPI } from '../../../services/api';

const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white";
const labelCls = "block text-xs font-medium text-gray-700 mb-1";

const TaskEditDrawer = ({ task, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [form, setForm] = useState({
    subject: '', type: '', dueDate: '', priority: '',
    assignedTo: null, status: '', linkedContact: null,
    linkedAccount: null, description: '', notes: '', shared: false,
  });

  // Fetch contacts, accounts and employees on mount
  useEffect(() => {
    const fetchData = async () => {
      setContactsLoading(true);
      setAccountsLoading(true);
      setEmployeesLoading(true);
      try {
        const [contactsRes, companiesRes, employeesRes] = await Promise.all([
          contactsAPI.getAllContacts(),
          companiesAPI.getAllCompanies(),
          employeesAPI.getAllEmployees()
        ]);

        const formattedContacts = (contactsRes.data || []).map(c => ({
          value: c.id,
          label: `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Unknown'
        }));

        const formattedAccounts = (companiesRes.data || []).map(c => ({
          value: c.id,
          label: c.companyName || c.name || 'Unknown'
        }));

        const employeeData = Array.isArray(employeesRes.data) ? employeesRes.data : [];
        const formattedEmployees = employeeData
          .map(u => ({
            value: u.id,
            label: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unknown',
            roles: u.roles || [],
          }))
          .filter(u => u.label !== 'Unknown' && Array.isArray(u.roles) && u.roles.some(r => r === 'CRM' || r === 'ROLE_CRM'));

        setContacts(formattedContacts);
        setAccounts(formattedAccounts);
        setEmployees(formattedEmployees);
      } catch (error) {
        console.error('Failed to fetch contacts/accounts/employees:', error);
      } finally {
        setContactsLoading(false);
        setAccountsLoading(false);
        setEmployeesLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!task) return;
    // Parse existing dueDate — extract date + time for the datetime-local input (YYYY-MM-DDTHH:mm)
    let dueDateValue = '';
    if (task.dueDate) {
      // task.dueDate might be "2025-09-10T10:00:00" or (legacy) "2025-09-10"
      dueDateValue = task.dueDate.substring(0, 16);
    }

    // Find matching contact, account and assignee options (by id); fall back to a
    // synthetic option showing the raw stored value for legacy non-id data (F38)
    const linkedContactOption = task.linkedContact ?
      contacts.find(c => c.value === task.linkedContact) || { value: task.linkedContact, label: task.linkedContact } :
      null;
    const linkedAccountOption = task.linkedAccount ?
      accounts.find(a => a.value === task.linkedAccount) || { value: task.linkedAccount, label: task.linkedAccount } :
      null;
    const assignedToOption = task.assignedTo ?
      employees.find(e => e.value === task.assignedTo) || { value: task.assignedTo, label: task.assignedTo } :
      null;

    setForm({
      subject:       task.subject       || '',
      type:          task.type          || '',
      dueDate:       dueDateValue,
      priority:      task.priority      || '',
      assignedTo:    assignedToOption,
      status:        task.status        || '',
      linkedContact: linkedContactOption,
      linkedAccount: linkedAccountOption,
      description:   task.description   || '',
      notes:         task.notes         || '',
      shared:        task.shared        || false,
    });
  }, [task, contacts, accounts, employees]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.subject?.trim()) { toast.error('Subject is required'); return; }

    // Convert dueDate from "YYYY-MM-DDTHH:mm" → "YYYY-MM-DDTHH:mm:00" (naive local time, no UTC conversion)
    const payload = {
      ...form,
      assignedTo:    form.assignedTo?.value || '',
      linkedContact: form.linkedContact?.value || '',
      linkedAccount: form.linkedAccount?.value || '',
      dueDate: form.dueDate ? `${form.dueDate}:00` : null,
    };

    setLoading(true);
    try { await onSave(task.id, payload); }
    finally { setLoading(false); }
  };

  if (!task) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col animate-slide-in">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white text-sm font-bold">
              {task.subject?.charAt(0)?.toUpperCase() || 'T'}
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Edit Task</h2>
              <p className="text-xs text-gray-500 truncate max-w-[220px]">{task.subject}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-200 text-gray-500 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* Basic Info */}
          <section>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Basic Info</p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Subject <span className="text-red-500">*</span></label>
                <input type="text" name="subject" value={form.subject}
                  onChange={handleChange} placeholder="Task subject" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Type</label>
                  <select name="type" value={form.type} onChange={handleChange} className={inputCls}>
                    <option value="">Select Type</option>
                    <option value="CALL">Call</option>
                    <option value="EMAIL">Email</option>
                    <option value="MEETING">Meeting</option>
                    <option value="FOLLOW_UP">Follow Up</option>
                    <option value="DEMO">Demo</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Due Date &amp; Time</label>
                  {/* datetime-local gives YYYY-MM-DDTHH:mm; we append :00 on submit (naive local time) */}
                  <input type="datetime-local" name="dueDate" value={form.dueDate}
                    onChange={handleChange} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Priority</label>
                  <select name="priority" value={form.priority} onChange={handleChange} className={inputCls}>
                    <option value="">Select Priority</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Status</label>
                  <select name="status" value={form.status} onChange={handleChange} className={inputCls}>
                    <option value="">Select Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* Assignment */}
          <section>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Assignment</p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Assigned To</label>
                <Select
                  options={employees}
                  value={form.assignedTo}
                  onChange={(opt) => handleSelectChange('assignedTo', opt)}
                  isLoading={employeesLoading}
                  isClearable
                  placeholder="Select team member"
                  className="text-sm"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="task-shared" name="shared" checked={form.shared}
                  onChange={(e) => setForm(prev => ({ ...prev, shared: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <label htmlFor="task-shared" className="text-xs font-medium text-gray-700">
                  Shared (visible to all org members)
                </label>
              </div>
              <div>
                <label className={labelCls}>Linked Contact</label>
                <Select
                  options={contacts}
                  value={form.linkedContact}
                  onChange={(opt) => handleSelectChange('linkedContact', opt)}
                  isLoading={contactsLoading}
                  isClearable
                  placeholder="Select Contact"
                  className="text-sm"
                  classNamePrefix="react-select"
                />
              </div>
              <div>
                <label className={labelCls}>Linked Account</label>
                <Select
                  options={accounts}
                  value={form.linkedAccount}
                  onChange={(opt) => handleSelectChange('linkedAccount', opt)}
                  isLoading={accountsLoading}
                  isClearable
                  placeholder="Select Account"
                  className="text-sm"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
          </section>

          {/* Notes */}
          <section>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Notes</p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Description</label>
                <textarea name="description" value={form.description} onChange={handleChange}
                  placeholder="Task description" rows={3}
                  className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className={labelCls}>Notes</label>
                <textarea name="notes" value={form.notes} onChange={handleChange}
                  placeholder="Additional notes" rows={3}
                  className={`${inputCls} resize-none`} />
              </div>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t bg-gray-50 flex gap-3 justify-end">
          <button onClick={onClose} disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-60">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-60">
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
};

export default TaskEditDrawer;